import { cn } from '@/lib/utils';
import { displayNameOf, initialsOf } from './accountIdentity';
import { accentFor } from './identityAccent';

/**
 * One person, as small as identity gets: their initials in a neutral disc.
 *
 * Initials rather than a picture because the contract has no picture to show —
 * `UserSummary` carries `id`, `email` and `display_name` and nothing else — so a
 * component that took an image URL would be describing a field the API does not
 * have.
 *
 * Tinted from the same eight slots the labels and workspace monograms use, keyed
 * on the person's id. This was `bg-muted` on the argument that colour belongs to
 * what a task is *doing* and author hues would compete with the rails — which
 * was half right and half wrong. It holds for *saturated* colour, the register
 * the state rails and spinners work in. These eight are pale label tints, and
 * cards already carry them on label chips, so the register was never free in the
 * first place; a grey disc among them just read as an element nobody had
 * finished. No texture here: at `size-5` shapes behind two letters are noise,
 * not atmosphere.
 *
 * Whether it *names* anyone is the caller's decision, because the answer differs
 * by surface and getting it wrong is invisible: pass `label` where the disc is
 * the only thing identifying the person (a task card), omit it where something
 * beside it already does (the rail, whose menu trigger is what gets the name).
 * A disc that is announced twice and a disc that is never announced at all look
 * identical on screen.
 *
 * `size-5` is a default, not a fixture — `cn` resolves a later `size-*` from
 * `className`, so a 28px rail disc is `className="size-7"`.
 */
export function UserAvatar({
  id,
  displayName,
  email,
  label,
  className,
}: {
  /** Keyed on the id, never the name: a rename must not change a person's disc. */
  id: string;
  displayName: string;
  email: string;
  /** How assistive technology announces it. Omit to mark the disc decorative. */
  label?: string;
  className?: string;
}) {
  const name = displayNameOf(displayName, email);
  const named = label !== undefined;

  return (
    <span
      role={named ? 'img' : undefined}
      aria-label={label}
      aria-hidden={named ? undefined : true}
      /* The address, not just the name: two colleagues sharing a display name is
         exactly the case initials cannot distinguish. Only where the disc is
         announced — a tooltip on a decorative element is a fact with no owner. */
      title={named ? `${name} · ${email}` : undefined}
      className={cn(
        // Two initials in a 20px disc are a glyph, not text: no step on the
        // scale fits inside the circle.
        // type-scale: glyph in a 20px disc
        'flex size-5 shrink-0 items-center justify-center rounded-full text-[0.5625rem] font-medium',
        accentFor(id),
        className,
      )}
    >
      {initialsOf(displayName, email)}
    </span>
  );
}
